import { useState } from "react";
import { Menu, X, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "./Logo";
import AuthModal from "./AuthModal";
import { useAuth } from "@/hooks/useAuth";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, signOut } = useAuth();

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/', { replace: true });
      setTimeout(() => {
        const section = document.getElementById(sectionId) || document.querySelector(`[data-section="${sectionId}"]`);
        if (section) section.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const section = document.getElementById(sectionId) || document.querySelector(`[data-section="${sectionId}"]`);
      if (section) section.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  const navLinks = [
    { label: "Flex Travel", href: "/travel", type: "marketplace", weight: "font-semibold" },
    { label: "Flex Education", href: "/education", type: "marketplace", weight: "font-semibold" },
    { label: "Flex Healthcare", href: "/healthcare", type: "marketplace", weight: "font-semibold" },
    { label: "How it Works", action: () => scrollToSection("how-it-works"), type: "info", weight: "font-normal" },
    { label: "Partners", href: "/partners", type: "info", weight: "font-normal" },
    { label: "FAQ", action: () => scrollToSection("faq"), type: "info", weight: "font-normal" },
    { label: "Contact", href: "/contact", type: "contact", weight: "font-normal" },
  ];

  const handleAuthAction = () => {
    if (isAuthenticated) {
      navigate('/account/dashboard');
    } else {
      setAuthModalOpen(true);
    }
  };

  return (
    <>
      <nav 
        className="fixed top-0 left-0 right-0 z-50 bg-background backdrop-blur-md border-b border-border shadow-sm"
        role="navigation"
        aria-label="Main navigation"
      >
        <a 
          href="#main-content" 
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-primary text-primary-foreground px-4 py-2 rounded-md z-[60]"
        >
          Skip to main content
        </a>
        
        <div className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex-shrink-0 mr-8">
            <Logo />
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => {
              const baseClasses = `font-body ${link.weight} smooth-transition focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg px-3 py-2`;
              const colorClasses = "text-foreground hover:text-primary";
              
              return (
                <div key={link.label}>
                  {link.href ? (
                    link.href.startsWith('mailto:') ? (
                      <a href={link.href} className={`${baseClasses} ${colorClasses}`}>{link.label}</a>
                    ) : (
                      <button onClick={() => navigate(link.href!)} className={`${baseClasses} ${colorClasses}`}>{link.label}</button>
                    )
                  ) : (
                    <button onClick={link.action} className={`${baseClasses} ${colorClasses}`}>{link.label}</button>
                  )}
                </div>
              );
            })}
            <Button 
              variant={isAuthenticated ? "outline" : "gradient"}
              size="sm" 
              onClick={handleAuthAction}
              className="ml-4"
            >
              {isAuthenticated ? (
                <span className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  My Account
                </span>
              ) : (
                'Sign In'
              )}
            </Button>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" aria-label="Open navigation menu" className="p-2">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] bg-background/95 backdrop-blur-md">
                <div className="flex flex-col space-y-6 mt-8">
                  <div className="flex justify-between items-center">
                    <Logo />
                    <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)} aria-label="Close navigation menu">
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                  
                  <div className="flex flex-col space-y-3">
                    {navLinks.map((link) => {
                      const mobileBaseClasses = `text-left text-lg font-body ${link.weight} py-3 px-4 smooth-transition focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg block w-full hover:bg-accent/10`;
                      const mobileColorClasses = "text-foreground hover:text-primary";
                      
                      return (
                        <div key={link.label}>
                          {link.href ? (
                            link.href.startsWith('mailto:') ? (
                              <a href={link.href} className={`${mobileBaseClasses} ${mobileColorClasses}`}>{link.label}</a>
                            ) : (
                              <button onClick={() => { navigate(link.href!); setIsOpen(false); }} className={`${mobileBaseClasses} ${mobileColorClasses}`}>{link.label}</button>
                            )
                          ) : (
                            <button onClick={link.action} className={`${mobileBaseClasses} ${mobileColorClasses}`}>{link.label}</button>
                          )}
                        </div>
                      );
                    })}
                    
                    <Button 
                      variant={isAuthenticated ? "outline" : "gradient"}
                      onClick={() => { handleAuthAction(); setIsOpen(false); }}
                      className="mt-6 w-full"
                    >
                      {isAuthenticated ? 'My Account' : 'Sign In'}
                    </Button>

                    {isAuthenticated && (
                      <Button
                        variant="ghost"
                        onClick={() => { signOut(); setIsOpen(false); }}
                        className="w-full text-muted-foreground"
                      >
                        Sign Out
                      </Button>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />
    </>
  );
};

export default Navigation;
